import { useEffect } from 'react';
import { Button, Container, Nav, Navbar, NavDropdown } from 'react-bootstrap';
import { useLocation, Link, NavLink, useNavigate } from 'react-router-dom';
import {useQuery} from "@tanstack/react-query";
import {fetchEnabledReports} from "../api/appShellApi";
import {logout, fetchAuthStatus} from "../api/authApi";

export function AppShell({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isLoginPage = location.pathname === '/login';
  const isClientPortal = location.pathname.startsWith('/client');

  const { data: authData, isLoading: isAuthLoading } = useQuery({
    queryKey: ['auth-status'],
    queryFn: fetchAuthStatus,
  });

  const hasInternalAccess = authData?.user?.permissions?.includes('agent_portal.internal_site_access');

  const {data, isLoading, isError} = useQuery({
    queryKey: ['enabled-reports'],
    queryFn: fetchEnabledReports,
    staleTime: 60 * 60 * 24,
    enabled: !isLoginPage && !!hasInternalAccess,
  })

  var enabledReportsList = [];
  if (!isLoading && !isError && data) {
    data.reports.forEach(report => {
      enabledReportsList.push(<NavDropdown.Item key={report.slug} as={NavLink} to={"/reports/" + report.slug}>{report.name}</NavDropdown.Item>)
    })
  }

  const username = authData?.user?.username || 'Guest';
  const odooUrl = authData?.odoo_url || '';
  const isAuthenticated = !!authData?.user;

  useEffect(() => {
    if (!isAuthLoading && !authData?.user && !isLoginPage) {
      navigate('/login');
    } else if (!isAuthLoading && authData?.user && isLoginPage) {
      navigate('/');
    } else if (!isAuthLoading && authData?.user && !isClientPortal && !authData?.user?.permissions?.includes('agent_portal.internal_site_access')) {
      navigate('/client');
    }
  }, [authData, isAuthLoading, isLoginPage, isClientPortal, navigate]);

  return (
    <div className="min-vh-100 page-wrapper">
      {isAuthenticated && !isLoginPage && !isClientPortal && (
        <Navbar expand="lg" className="sticky-top border-bottom navbar-custom navbar-z">
          <Container fluid>
            <Navbar.Brand as={Link} to="/">
              Netflix2 <small><strong>{authData?.version || ''}</strong></small>
            </Navbar.Brand>
            <Navbar.Toggle aria-controls="main-nav" />
            <Navbar.Collapse id="main-nav">
              <Nav className="me-auto">
                <Nav.Link as={NavLink} to="/">Dashboard</Nav.Link>
                {/*<Nav.Link as={NavLink} to="/client">Customer Portal</Nav.Link>*/}
                { enabledReportsList.length > 0 &&
                  <NavDropdown title="Reports" id="nav-reports">
                    {enabledReportsList}
                  </NavDropdown>
                }
                <Nav.Link as={NavLink} to="/agent/po">Purchase orders</Nav.Link>
                <Nav.Link as={NavLink} to="/agent/so">Sales orders</Nav.Link>
                <Nav.Link as={NavLink} to="/agent/contacts">Contact search</Nav.Link>
                {authData?.user?.permissions?.includes('agent_portal.bo_tools') && (
                  <Nav.Link as={NavLink} to="/agent/costref-search">Costref Search</Nav.Link>
                )}
                {authData?.user?.permissions?.includes('intransit.limited_intransit') && (
                  <NavDropdown title="Intransit" id="nav-intransit">
                    <NavDropdown.Item as={NavLink} to="/intransit/purchases">Purchases</NavDropdown.Item>
                    <NavDropdown.Item as={NavLink} to="/intransit/sales">Sales</NavDropdown.Item>
                    <NavDropdown.Item as={NavLink} to="/intransit/quotations">Quotations</NavDropdown.Item>
                    <NavDropdown.Item as={NavLink} to="/intransit/arriving">Arriving stock</NavDropdown.Item>
                    <NavDropdown.Item as={NavLink} to="/intransit/internal">Internal transfers</NavDropdown.Item>
                  </NavDropdown>
                )}
                {authData?.user?.is_staff && (
                  <Nav.Link href="/admin/" target="_blank" rel="noreferrer">Django Admin</Nav.Link>
                )}
              </Nav>
              <Navbar.Text className="me-4 py-0 text-center d-flex flex-column lh-sm">
                <span>Logged in as <strong>{username}</strong></span>
                {odooUrl && (
                  <small className="text-body-secondary">
                    <a 
                      href={`https://${odooUrl}/web`}
                      target="_blank" 
                      rel="noreferrer"
                      className="text-decoration-none text-inherit"
                    >
                      {odooUrl}
                    </a>
                  </small>
                )}
              </Navbar.Text>
              <Button variant="outline-success" size="sm" onClick={() => logout()}>Log out</Button>
            </Navbar.Collapse>
          </Container>
        </Navbar>
      )}
      {!isClientPortal &&
          <Container fluid>
            {children}
          </Container>
      }{ isClientPortal && children}
    </div>
  );
}
